import React from "react";
import { Icon, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Colors from "../../../../../themes/Colors";
import InfoCard from "./InfoCard";
import { SeparateNumberFormat } from "../../../../../services/utils";
import PriceBreakdownTooltip from "../PriceBreakdownTooltip";
import {
  formatMoney,
  getStatusInfo,
  isInForce,
  getDurationDays,
  getPurchasedSpots,
  getScreenCount,
  getAmounts,
  getCreatorName,
  getCountry,
  getDspName,
} from "../../utils/campaignDetailData";

const TONE_COLORS = {
  active: Colors.green,
  next: Colors.primary,
  pending: Colors.atention,
  previous: "#9aa0a6",
  paused: Colors.atention,
  cancelled: Colors.red,
  proposal: Colors.primary,
};

const fmtDate = (d) => (d ? moment(d).format("DD/MM/YY") : "—");

const CampaignSummaryCard = ({ campaign, mode, realSpots, isCpmi, onEditDates }) => {
  const isProposal = mode === "proposal";
  const { t, i18n } = useTranslation();
  if (!campaign) return null;

  const status = getStatusInfo(campaign, t);
  const inForce = isInForce(campaign);
  const duration = getDurationDays(campaign);
  const purchased = getPurchasedSpots(campaign);
  const screenCount = getScreenCount(campaign);
  const amounts = getAmounts(campaign);

  const dateRange = `${fmtDate(campaign.start_date)} - ${fmtDate(
    campaign.end_date
  )}`;

  return (
    <div className="campaignDetail__card">
      <h1 className="campaignDetail__title">
        {campaign.name}
        {!isProposal && ` [Id.: ${campaign.id}]`}
      </h1>
      <p className="campaignDetail__subtitle">
        {campaign.smart_campaign
          ? t("Programmatic campaign")
          : t("Traditional campaign")}
      </p>

      <h3 className="campaignDetail__section-title">{t("Campaign status")}</h3>
      <div className="campaignDetail__grid">
        <InfoCard
          dotColor={TONE_COLORS[status.tone] || "#9aa0a6"}
          label={t("Status")}
          value={status.label}
        />
        <InfoCard
          icon="calendar"
          label={t("Exhibition date")}
          value={dateRange}
          extra={
            onEditDates ? (
              <Tooltip
                title={
                  campaign.active_change_requests
                    ? t(
                      "It is necessary for the pending date changes to be approved in order to be able to edit again."
                    )
                    : t("Edit exhibition dates")
                }
              >
                <Icon
                  type="edit"
                  onClick={
                    campaign.active_change_requests ? undefined : onEditDates
                  }
                  style={{
                    cursor: campaign.active_change_requests
                      ? "not-allowed"
                      : "pointer",
                    color: campaign.active_change_requests
                      ? Colors.grayMedium
                      : Colors.primary,
                  }}
                />
              </Tooltip>
            ) : null
          }
        />
        <InfoCard
          icon="column-width"
          label={t("Duration")}
          value={`${duration} ${t("Day")}${duration === 1 ? "" : "s"}`}
        />
        <InfoCard
          icon="carry-out"
          label={t("Condition")}
          value={inForce ? t("Valid") : t("Expired")}
        />
        {!isProposal && (
          <InfoCard
            icon="eye"
            label={t("Real spots")}
            value={realSpots != null ? SeparateNumberFormat(realSpots) : "—"}
          />
        )}
        {/* cpmi companies never see purchased (projected) playouts */}
        {!isCpmi && (
          <InfoCard
            icon="play-square"
            label={t("Purchased spots")}
            value={SeparateNumberFormat(purchased)}
          />
        )}
        <InfoCard
          icon="desktop"
          label={t("Screens quantity")}
          value={SeparateNumberFormat(screenCount)}
        />
      </div>

      <h3 className="campaignDetail__section-title">{t("Campaign details")}</h3>
      <div className="campaignDetail__grid">
        <InfoCard
          icon="idcard"
          label={t("Created by")}
          value={getCreatorName(campaign)}
        />
        <InfoCard
          icon="calendar"
          label={t("Created on")}
          value={
            campaign.created_at
              ? moment(campaign.created_at).format("dddd - DD/MM/YY")
              : "—"
          }
        />
        <InfoCard
          icon="environment"
          label={t("Country")}
          value={getCountry(campaign)}
        />
        <InfoCard
          icon="user"
          label={t("Client")}
          value={(campaign.client && campaign.client.name) || "—"}
        />
        <InfoCard
          icon="tag"
          label={t("Brand")}
          value={(campaign.brand && campaign.brand.name) || "—"}
        />
        <InfoCard icon="desktop" label={t("DSP")} value={getDspName(campaign)} />
      </div>

      <h3 className="campaignDetail__section-title">{t("Amounts")}</h3>
      <div className="campaignDetail__amounts">
        <div className="campaignDetail__amount">
          <Icon type="dollar" className="campaignDetail__amount-icon" />
          <div>
            <span className="campaignDetail__amount-label">{t("Subtotal")}</span>
            <span className="campaignDetail__amount-value">
              {formatMoney(amounts.subtotal, amounts.currency, i18n.language)}
            </span>
          </div>
        </div>
        <div className="campaignDetail__amount">
          <Icon type="dollar" className="campaignDetail__amount-icon" />
          <div>
            <span className="campaignDetail__amount-label">
              {t("Total budget")}
            </span>
            <span
              className="campaignDetail__amount-value campaignDetail__amount-value--bold"
              style={{ display: "flex", alignItems: "center", gap: "2px" }}
            >
              {formatMoney(amounts.total, amounts.currency, i18n.language)}
              {campaign.id && (
                <PriceBreakdownTooltip
                  campaignId={campaign.id}
                  currency={amounts.currency}
                />
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignSummaryCard;
